/**
 * Tiny scroll-position memory backed by sessionStorage.
 *
 * Used so pages like /work can restore the user's scroll position when they
 * come back from a detail page, instead of being snapped to the top by
 * the global ScrollToTop reset.
 */

const PREFIX = "scroll:";
const MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

type Entry = { y: number; ts: number };

const storage = (): Storage | null => {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

const read = (key: string): Entry | null => {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Entry;
    if (!parsed || typeof parsed.y !== "number" || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > MAX_AGE_MS) {
      s.removeItem(PREFIX + key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const saveScroll = (key: string): void => {
  const s = storage();
  if (!s) return;
  const winY = typeof window !== "undefined" ? window.scrollY || window.pageYOffset || 0 : 0;
  let y = winY;
  // Some pages use an inner [data-scroll-container] (e.g. homepage). If the
  // window itself didn't scroll, fall back to the inner container.
  if (y === 0 && typeof document !== "undefined") {
    const inner = document.querySelector<HTMLElement>("[data-scroll-container]");
    if (inner && inner.scrollTop > 0) y = inner.scrollTop;
  }
  try {
    s.setItem(PREFIX + key, JSON.stringify({ y, ts: Date.now() } as Entry));
  } catch {
    /* ignore quota errors */
  }
};

export const peekScroll = (key: string): number | null => {
  const entry = read(key);
  return entry ? entry.y : null;
};

export const consumeScroll = (key: string): number | null => {
  const entry = read(key);
  if (!entry) return null;
  const s = storage();
  s?.removeItem(PREFIX + key);
  return entry.y;
};
